import React, { useEffect, useMemo, useState } from "react";
import { msmartAxios, muAxios } from "../../api/axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Link } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../utils/errorMessage";
import { CardGridSkeleton } from "../../components/Skeleton";

function Courses() {
  const { user } = useAuthContext();
  const { notifyError, notifyPromise } = useToast();

  const [courses, setCourses] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const [progress, setProgress] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function fetchCourses() {
      try {
        setLoadingCourses(true);

        const res = await msmartAxios.get("/api/msmart/get/courses", {
          headers: { accessToken: user.token },
        });

        if (!mounted) return;

        if (res.data) setCourses(res.data);
        else setErrMsg("Cannot access courses at this moment. Please try again later.");
      } catch (e) {
        if (!mounted) return;
        setErrMsg(getErrorMessage(e, "Cannot access courses at this moment. Please try again later."));
      } finally {
        if (mounted) setLoadingCourses(false);
      }
    }

    if (user?.token) fetchCourses();
    return () => (mounted = false);
  }, [user?.token]);

  useEffect(() => {
    let mounted = true;

    async function fetchProgress() {
      try {
        setLoadingProgress(true);

        const res = await muAxios.get(`/api/mu/get/progress`, {
          headers: { accessToken: user.token },
        });

        if (!mounted) return;

        const json = res.data;
        if (json?.progress) setProgress(json.progress);
      } catch (e) {
        // progress fail tak perlu block
      } finally {
        if (mounted) setLoadingProgress(false);
      }
    }

    if (user?.token) fetchProgress();
    return () => (mounted = false);
  }, [user?.token]);

  const progressMap = useMemo(() => {
    const map = new Map();
    (progress || []).forEach((p) => {
      if (p?.shortlink) map.set(p.shortlink, p);
    });
    return map;
  }, [progress]);

  const registerCourse = async (courseShortlink) => {
    try {
      const promise = muAxios.get(`/api/mu/register/${courseShortlink}`, {
        headers: { accessToken: user.token },
      });

      const res = await notifyPromise(
        promise,
        {
          loading: "Enrolling...",
          success: "Enrolled successfully. Opening course...",
          error: "Failed to enroll. Please try again.",
        },
        {
          position: "bottom-right",
          duration: 3000,
        }
      );

      // kalau backend bagi error custom
      if (res?.data?.err) {
        notifyError(res.data.err);
        return;
      }

      // success -> refresh untuk update progress button
      window.location.reload();
    } catch (e) {
      // toast.promise dah handle error msg, sini optional
    }
  };

  const isBusy = loadingCourses || loadingProgress;

  return (
    <div className="">
      <div className="container py-6">
        {/* Header */}
        <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-2 mb-4">
          <div>
            <h1 className="header-title mb-1">Training Centre</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
              Access courses, scripts & learning resources.
            </div>
          </div>

          <div className="text-muted" style={{ fontSize: 13 }}>
            {isBusy ? "Loading..." : `${courses.length} course(s) available`}
          </div>
        </div>

        {/* Error */}
        {errMsg && (
          <div className="alert alert-danger" role="alert">
            {errMsg}
          </div>
        )}

        {!errMsg && !isBusy && courses.length === 0 && (
          <div className="text-center text-muted py-5">
            No courses available right now.
          </div>
        )}

        {isBusy && <CardGridSkeleton count={6} imageHeight={160} />}

        {/* Grid */}
        <div className="row g-3">
          {(isBusy ? [] : courses).map((c) => {
            const toCourse = `/courses/${c.shortlink}`;
            const logo = process.env.REACT_APP_MU + c.logoFile;

            const p = progressMap.get(c.shortlink);
            const enrolled = !!p;

            const total = Number(c?.vidCount || 0);
            const current = Number(p?.currentLesson || 1);

            // completed = currentLesson - 1 (sebab currentLesson = next lesson)
            const completed = Math.max(0, Math.min(total, current - 1));

            const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
            const clamped = Math.max(0, Math.min(100, percent));

            return (
              <div key={c.shortlink} className="col-12 col-md-6 col-lg-4">
                <div className="card shadow-sm h-100" style={{ borderRadius: 14 }}>
                  {/* Cover */}
                  <div
                    style={{
                      height: 160,
                      borderTopLeftRadius: 14,
                      borderTopRightRadius: 14,
                      overflow: "hidden",
                      background: "#f6f7f9",
                    }}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <img
                      src={logo}
                      alt={c.shortlink}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>

                  <div className="card-body d-flex flex-column">
                    {/* Title + badge */}
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div>
                        <div className="fw-semibold" style={{ fontSize: 16 }}>
                          {c.courseName}
                        </div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {c.shortlink}
                        </div>
                      </div>

                      {enrolled ? (
                        <span className="badge text-bg-success">Enrolled</span>
                      ) : (
                        <span className="badge text-bg-light border">New</span>
                      )}
                    </div>

                    {/* Desc */}
                    <p
                      className="text-muted mt-3 mb-3"
                      style={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {c.description}
                    </p>

                    {/* Progress (optional) */}
                    {enrolled && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between text-muted" style={{ fontSize: 12 }}>
                          <span>Progress</span>
                          <span>{clamped}%</span>
                        </div>
                        <div className="progress" style={{ height: 8 }}>
                          <div className="progress-bar" role="progressbar" style={{ width: `${clamped}%` }} />
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-auto d-grid">
                      {enrolled ? (
                        <Link to={toCourse} className="btn btn-outline-dark">
                          View Chapters <i className="bi bi-arrow-right ms-1" />
                        </Link>
                      ) : (
                        <button className="btn btn-dark" onClick={() => registerCourse(c.shortlink)}>
                          Enroll Now <i className="bi bi-plus-circle ms-1" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="card-footer bg-transparent border-0 pt-0 pb-3 px-3">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Tip: Enroll first to unlock chapters.
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Courses;
